// src/pages/admin/EmailSettingsPage.tsx
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { apiClient, getErrorMessage } from '@/lib/api';
import { cn } from '@/lib/utils';
import Button from '@/components/ui/Button';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Notice } from '@/components/ui/Notice';
import { Skeleton } from '@/components/ui/Skeleton';
import { checkbox, fieldControl, fieldHint, fieldLabel } from '@/components/ui/fieldStyles';

interface SmtpConfig {
  host: string;
  port: number;
  secure: boolean;
  username: string;
  from_address: string;
  from_name: string;
  configured: boolean;
}

const EMPTY: SmtpConfig = {
  host: '', port: 587, secure: false, username: '', from_address: '', from_name: '', configured: false,
};

const EmailSettingsPage: React.FC = () => {
  const [form, setForm] = useState<SmtpConfig>(EMPTY);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get<SmtpConfig>('/admin/settings/smtp')
      .then(({ data }) => setForm({ ...EMPTY, ...data }))
      .catch(e => toast.error(getErrorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await apiClient.put<SmtpConfig>('/admin/settings/smtp', {
        host: form.host,
        port: form.port,
        secure: form.secure,
        username: form.username,
        from_address: form.from_address,
        from_name: form.from_name,
        ...(password ? { password } : {}),
      });
      setForm({ ...EMPTY, ...data });
      setPassword('');
      toast.success('SMTP settings saved');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl space-y-6" aria-hidden="true">
        <div><Skeleton className="h-8 w-56" /><Skeleton className="mt-2 h-4 w-80 max-w-full" /></div>
        <Skeleton className="h-12 rounded-2xl" />
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  const field = (id: string, label: React.ReactNode, input: React.ReactNode) => (
    <div>
      <label htmlFor={id} className={fieldLabel}>{label}</label>
      {input}
    </div>
  );

  return (
    <div className="max-w-2xl space-y-6">
      <PageHeader
        title="Email settings"
        description="Account emails, such as sign-in notices, go out through this SMTP server. Any standard provider works. Emails wait in a queue, so a slow server never holds up a request."
      />

      <Notice tone={form.configured ? 'success' : 'warning'} role="status">
        {form.configured ? 'A password is currently set.' : 'No password set yet. Outbound email will fail until one is saved.'}
      </Notice>

      <Panel title="SMTP server">
        <form onSubmit={handleSave} className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_8rem]">
            {field('smtp-host', 'SMTP host', (
              <input id="smtp-host" className={fieldControl} value={form.host}
                onChange={e => setForm(f => ({ ...f, host: e.target.value }))} placeholder="smtp.example.com" required />
            ))}
            {field('smtp-port', 'Port', (
              <input id="smtp-port" type="number" className={cn(fieldControl, 'font-mono tabular-nums')} value={form.port}
                onChange={e => setForm(f => ({ ...f, port: +e.target.value }))} required />
            ))}
          </div>

          <label className="flex items-start gap-2.5 text-sm text-foreground">
            <input type="checkbox" className={cn(checkbox, 'mt-0.5')} checked={form.secure} onChange={e => setForm(f => ({ ...f, secure: e.target.checked }))} />
            <span>
              Use implicit TLS (typically port 465).
              <span className="block text-xs text-muted-foreground">Leave unchecked for STARTTLS (typically port 587).</span>
            </span>
          </label>

          {field('smtp-username', 'Username', (
            <input id="smtp-username" className={fieldControl} value={form.username}
              onChange={e => setForm(f => ({ ...f, username: e.target.value }))} required />
          ))}

          <div>
            <label htmlFor="smtp-password" className={fieldLabel}>Password</label>
            <input id="smtp-password" type="password" className={fieldControl} value={password}
              onChange={e => setPassword(e.target.value)} placeholder={form.configured ? '••••••••' : ''} />
            {form.configured && <p className={fieldHint}>Leave blank to keep the current one.</p>}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {field('smtp-from-address', 'From address', (
              <input id="smtp-from-address" type="email" className={fieldControl} value={form.from_address}
                onChange={e => setForm(f => ({ ...f, from_address: e.target.value }))} required />
            ))}
            {field('smtp-from-name', 'From name', (
              <input id="smtp-from-name" className={fieldControl} value={form.from_name}
                onChange={e => setForm(f => ({ ...f, from_name: e.target.value }))} required />
            ))}
          </div>

          <div className="flex justify-end border-t border-border pt-4">
            <Button type="submit" loading={saving}>
              {saving ? 'Saving…' : 'Save settings'}
            </Button>
          </div>
        </form>
      </Panel>
    </div>
  );
};

export default EmailSettingsPage;
